import PropTypes from 'prop-types';
import { ArrowDownIcon, ArrowUpIcon, CheckCircleIcon } from '@chakra-ui/icons';
import {
  Box,
  Collapse,
  Flex,
  IconButton,
  Skeleton,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PublishTag from 'components/common/fields/PublishTag';
import { fetchSubQuestsByQuestIdAction } from '../../store/modules/progression/progression-actions';
import SubQuestItem from './SubQuestItem';

function QuestItem({
  classId,
  courseId,
  quest,
  isMinimalMode = false,
  activeQuestId = null,
  activeSubQuestId = null,
}) {
  const [isToggled, setIsToggled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const dispatch = useDispatch();

  const completedQuestSubQuest = useSelector(
    (state) =>
      state.progression?.progressions?.filter(
        (p) => p.questId === quest.id && p.dateFinished
      )?.length || 0
  );
  const questSubQuestsData = useSelector((state) =>
    state.progression?.questSubQuests?.find((q) => q.questId === quest.id)
  );

  const subQuests = useMemo(() => {
    const allSubQuests = questSubQuestsData?.subQuests || [];
    return allSubQuests;
  }, [questSubQuestsData]);

  const totalSubQuestCount = subQuests.length;
  const isCompleted =
    totalSubQuestCount !== 0 && completedQuestSubQuest === totalSubQuestCount;

  useEffect(() => {
    if (subQuests.length === 0) {
      setIsLoading(true);
      dispatch(
        fetchSubQuestsByQuestIdAction({ classId, courseId, questId: quest.id })
      );
    } else {
      setIsLoading(false);
    }
  }, [dispatch, courseId, classId, quest.id, subQuests.length]);

  useEffect(() => {
    if (subQuests.length > 0) {
      setIsLoading(false);
    }
  }, [subQuests.length]);

  const onToggleHandler = () => {
    setIsToggled(!isToggled);
  };

  const checkIconColor = useColorModeValue('green', 'white');

  return (
    <Box
      mb={isMinimalMode ? 0 : 6}
      boxShadow={activeQuestId === quest.id ? 'md' : ''}
      borderWidth={isMinimalMode ? '' : '1px'}
      borderRadius={isMinimalMode ? '' : 'md'}
    >
      <Box
        w="100%"
        p={isMinimalMode ? 1 : 4}
        bg={useColorModeValue(
          activeQuestId === quest.id ? '#FFDFDF' : '#f7f7f7',
          activeQuestId === quest.id ? 'teal' : 'gray.600'
        )}
      >
        <Flex align="center">
          <IconButton
            onClick={() => onToggleHandler()}
            variant="plain"
            aria-label={isToggled ? 'Untoggle quest' : 'Toggle quest'}
            icon={isToggled ? <ArrowUpIcon /> : <ArrowDownIcon />}
          />

          <Text
            fontWeight={500}
            fontSize={isMinimalMode ? 'xs' : 'md'}
            flex={1}
          >
            {quest?.name}
          </Text>

          {isCompleted > 0 && (
            <CheckCircleIcon boxSize={4} color={checkIconColor} />
          )}
          {!quest.isPublished && (
            <Box ml={2}>
              <PublishTag isPublished={quest.isPublished} />
            </Box>
          )}
        </Flex>
      </Box>

      <Collapse in={isToggled}>
        <Box p={4}>
          {!isMinimalMode && (
            <Text mb={5} fontSize={isMinimalMode ? 'xs' : 'md'}>
              {quest?.description}
            </Text>
          )}

          {isLoading ? (
            <Stack>
              <Skeleton
                mb={isMinimalMode ? 2 : 6}
                height={isMinimalMode ? '42px' : '72px'}
                borderRadius="md"
              />
              <Skeleton
                mb={isMinimalMode ? 2 : 6}
                height={isMinimalMode ? '42px' : '72px'}
                borderRadius="md"
              />
            </Stack>
          ) : (
            <Box>
              {subQuests?.map((subQuest) => (
                <SubQuestItem
                  key={subQuest.id}
                  classId={classId}
                  courseId={courseId}
                  questId={quest.id}
                  subQuest={subQuest}
                  isMinimalMode={isMinimalMode}
                  activeSubQuestId={activeSubQuestId}
                />
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

QuestItem.defaultProps = {
  classId: 0,
  isMinimalMode: false,
  activeSubQuestId: 0,
  activeQuestId: 0,
};

QuestItem.propTypes = {
  classId: PropTypes.number,
  courseId: PropTypes.number.isRequired,
  quest: PropTypes.object.isRequired,
  isMinimalMode: PropTypes.bool,
  activeQuestId: PropTypes.number,
  activeSubQuestId: PropTypes.number,
};

export default QuestItem;
